import { ChartOptions } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useDashboardStore } from '../../store/useDashboardStore';
import { MostUsedFeaturesQuery, MostUsedFeaturesQueryVariables } from '@/graphql/generated/graphql';
import { MOST_USED_FEATURES } from '@/graphql/queries/report/ga-interactions';
import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import useTranslation from '@/hooks/i18n';
import { getColor } from '../../utils/colors';
import { useAdminStore } from '@/pages/admin/store/useAdmin.store';

interface DataType {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string[];
  }[];
}

export default function MostUsedFeatures() {
  const { page } = useAdminStore();

  const [data, setData] = useState<DataType>({
    labels: [],
    datasets: [
      {
        label: '',
        data: [],
        backgroundColor: [],
      },
    ],
  });
  const { t } = useTranslation();
  const { getDateRange, schoolsIds } = useDashboardStore();
  const { data: response, loading } = useQuery<MostUsedFeaturesQuery, MostUsedFeaturesQueryVariables>(
    MOST_USED_FEATURES,
    {
      variables: {
        startDate: getDateRange()?.startDate,
        endDate: getDateRange()?.endDate,
        schoolsIds,
      },
    },
  );

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const total = tooltipItem.dataset.data.reduce((sum, value) => sum + value, 0);
            const currentValue = tooltipItem.raw as number;
            const percentage = ((currentValue / total) * 100).toFixed(2);
            const label = tooltipItem.label || '';
            return `${label}: ${percentage}%`;
          },
        },
      },
    },
  };

  useEffect(() => {
    if (response && response.getFeatureUsage) {
      const features = response.getFeatureUsage
        .map((feature) => feature.features)
        .flatMap((features) => features)
        .reduce((obj: { [key: string]: number }, current) => {
          if (obj[current!.feature!]) {
            obj[current!.feature!] += current?.pageViewsCount || 0;
          } else {
            obj[current!.feature!] = current?.pageViewsCount || 0;
          }
          return obj;
        }, {});

      // Convert features object to an array of entries and sort by values
      const sortedEntries = Object.entries(features).sort(([, a], [, b]) => b - a);

      // Map sorted entries to labels and data
      const sortedLabels = sortedEntries.map(([feature]) => t(feature));
      const sortedData = sortedEntries.map(([, count]) => count);

      setData({
        labels: sortedLabels,
        datasets: [
          {
            data: sortedData,
            backgroundColor: sortedEntries.map((_, index) => getColor(index)),
            label: '',
          },
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center">
          <div className="size-64 animate-pulse rounded-full bg-gray-300"></div>
        </div>
      );
    }

    if (data.labels.length === 0) {
      return (
        <div className="flex items-center justify-center">
          {/* //Empty state of a doughnut chart */}
          <div className="flex flex-col items-center justify-center">
            <p className="text-sh-xs text-gray-500">No data available</p>
          </div>
        </div>
      );
    }
    return <Doughnut data={data} options={options} style={{ width: 'auto' }} />;
  };
  return (
    <>
      <div className="border-b-2 border-[#ECECEC] px-11 py-6">
        <p className=" text-sh-xs font-bold">
          {page?.dashboard?.chartsTitles?.mostUsedFeatures || 'Most used features'}
        </p>
      </div>
      <div className=" p-5">{renderContent()}</div>
    </>
  );
}
